import { CircleNotch } from "@phosphor-icons/react";

import { ArtifactListCard } from "./ArtifactListCard";
import { ReviewListCard } from "./ReviewListCard";

import {
  type ChatArtifact,
  type ChatArtifactOpenPayload,
} from "../artifactTypes";

/**
 * Cowork-style artifact sidebar: Reviews + Documents (canvas) sections.
 * Sections size to their content; the parent rail slot applies min/max height.
 */
export function ChatArtifactsPanel({
  reviewArtifacts,
  canvasArtifacts,
  isArtifactActive,
  onOpenArtifact,
  isArtifactUnread,
  reviewsStatus,
  testIdPrefix = "chat-artifacts",
}: {
  reviewArtifacts: readonly ChatArtifact[];
  canvasArtifacts: readonly ChatArtifact[];
  isArtifactActive: (artifact: ChatArtifact) => boolean;
  onOpenArtifact: (payload: ChatArtifactOpenPayload) => void;
  isArtifactUnread?: (artifact: ChatArtifact) => boolean;
  reviewsStatus?: string;
  testIdPrefix?: string;
}) {
  const hasReviews = reviewArtifacts.length > 0;
  const hasDocuments = canvasArtifacts.length > 0;

  if (!hasReviews && !hasDocuments) return null;

  const rowTestIdPrefix =
    testIdPrefix === "campaign-artifacts"
      ? "campaign-artifact-row"
      : "chat-artifact-row";

  return (
    <div
      className="flex shrink-0 flex-col gap-3"
      data-testid={`${testIdPrefix}-panel`}
      aria-label="Chat artifacts"
    >
      {hasReviews ? (
        <section
          className="flex shrink-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white"
          aria-label="Reviews"
          data-testid={`${testIdPrefix}-reviews-section`}
          data-section-expanded="true"
        >
          <div className="flex w-full shrink-0 items-center bg-base-white px-4 py-3">
            <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
              Reviews
            </h2>
          </div>
          <div
            className="border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3"
            data-testid={`${testIdPrefix}-reviews-scroll`}
          >
            {reviewsStatus ? (
              <p
                className="text-puntt-yellow-11 mb-3 text-[11px] font-bold uppercase tracking-wide"
                data-testid={`${testIdPrefix}-reviews-status`}
              >
                {reviewsStatus}
              </p>
            ) : null}
            <div className="flex flex-col gap-2">
              {reviewArtifacts.map((artifact) => (
                <ReviewListCard
                  key={artifact.id}
                  data-testid={`${rowTestIdPrefix}-${artifact.id}`}
                  testIdPrefix={rowTestIdPrefix}
                  isActive={isArtifactActive(artifact)}
                  hasUnread={isArtifactUnread?.(artifact) ?? false}
                  unreadTestId={
                    testIdPrefix === "campaign-artifact-row"
                      ? `campaign-artifact-unread-${artifact.id}`
                      : `chat-artifact-unread-${artifact.id}`
                  }
                  item={{
                    id: artifact.id,
                    name: artifact.title,
                    versionNumber: artifact.versionNumber,
                    thumbnailUrl: artifact.thumbnailUrl,
                    aiCommentCount: artifact.aiCommentCount,
                    humanCommentCount: artifact.humanCommentCount,
                    isGenerating: artifact.isGenerating,
                  }}
                  onClick={() => {
                    if (artifact.ticketId && !artifact.isGenerating) {
                      onOpenArtifact({
                        kind: "review",
                        ticketId: artifact.ticketId,
                      });
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {hasDocuments ? (
        <section
          className="flex shrink-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white"
          aria-label="Documents"
          data-testid={`${testIdPrefix}-documents-section`}
          data-section-expanded="true"
        >
          <div className="flex w-full shrink-0 items-center bg-base-white px-4 py-3">
            <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
              Documents
            </h2>
          </div>
          <div
            className="border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3"
            data-testid={`${testIdPrefix}-documents-scroll`}
          >
            <div className="flex flex-col gap-2">
              {canvasArtifacts.map((artifact) => (
                <ArtifactListCard
                  key={artifact.id}
                  artifact={artifact}
                  isActive={isArtifactActive(artifact)}
                  hasUnread={isArtifactUnread?.(artifact) ?? false}
                  unreadTestId={
                    testIdPrefix === "campaign-artifact-row"
                      ? `campaign-artifact-unread-${artifact.id}`
                      : `chat-artifact-unread-${artifact.id}`
                  }
                  testIdPrefix={rowTestIdPrefix}
                  data-testid={`${rowTestIdPrefix}-${artifact.id}`}
                  onClick={() => {
                    onOpenArtifact({
                      kind: "canvas",
                      messageId: artifact.messageId,
                    });
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function ChatArtifactsPanelLoading() {
  return (
    <div
      className="flex items-center justify-center gap-2 rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-6 text-puntt-neutral-gray-11"
      data-testid="chat-artifacts-panel-loading"
      aria-label="Loading artifacts"
    >
      <CircleNotch size={18} className="animate-spin" aria-hidden="true" />
      <span className="text-sm">Loading...</span>
    </div>
  );
}
